import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { salsaAnalytics } from '../services/salsaAnalytics'

const TOUR_ID = 'onzoe-dashboard-welcome-v1'
const COMPLETED_KEY = 'onzoe-dashboard-tour-completed-v1'

interface OnzoeTourEventDetail {
  event_type: string
  tour_id: string
  session_id: string
  attributes: Record<string, string | number | boolean>
}

declare global {
  interface Window {
    OnzoeWidget?: {
      init: (config: Record<string, unknown>) => Promise<void>
      startTour: (tourId: string) => boolean
      refreshTours?: () => number
      open?: () => void
    }
  }
}

const dashboardTour = () => ({
  id: TOUR_ID,
  name: 'Onzoe product tour',
  start_url: `${window.location.origin}/dashboard`,
  narration_enabled: true,
  auto_start: window.localStorage.getItem(COMPLETED_KEY) !== 'true',
  accent_color: '#20D9FF',
  steps: [
    {
      id: 'command-center',
      selector: '[data-tour="dashboard-welcome"]',
      title: 'Your customer acquisition command center',
      body: 'This is Onzoe. The dashboard connects the agent, guided product experience, customer sessions, and measured outcomes in one place.',
      action: 'spotlight',
    },
    {
      id: 'install',
      selector: '[data-tour="dashboard-install"]',
      title: 'Launch the customer-facing experience',
      body: 'Install one managed Onzoe widget to answer questions, narrate tours, capture qualified interest, and preserve the visitor’s source.',
      action: 'spotlight',
    },
    {
      id: 'results',
      selector: '[data-tour="dashboard-results"]',
      title: 'Prove what happened after launch',
      body: 'Onzoe keeps the operational proof close: visitors, interactions, leads, session transcripts, conversion signals, and account readiness.',
      action: 'spotlight',
    },
    {
      id: 'tour-builder',
      selector: '[data-tour="nav-tours"]',
      title: 'Turn the product into the demo',
      body: 'Tours creates the live walkthrough and an editable video script. The Onzoe voice can narrate both the interactive experience and every channel-ready video.',
      action: 'spotlight',
    },
    {
      id: 'growth-loop',
      selector: '[data-tour="nav-growth"]',
      title: 'Know which asset earns the customer',
      body: 'Growth closes the loop from each LinkedIn, YouTube, or Instagram asset to qualified interest, signup, paid subscription, revenue, and production cost.',
      action: 'spotlight',
      conversion: true,
    },
  ],
})

export function DashboardProductTour() {
  const location = useLocation()
  const initialized = useRef(false)

  useEffect(() => {
    const handleTourEvent = (event: Event) => {
      const detail = (event as CustomEvent<OnzoeTourEventDetail>).detail
      if (!detail || detail.tour_id !== TOUR_ID) return
      salsaAnalytics.track(`dashboard_${detail.event_type}`, {
        tour_id: detail.tour_id,
        tour_session_id: detail.session_id,
        ...detail.attributes,
      })
      if (detail.event_type === 'tour_complete') window.localStorage.setItem(COMPLETED_KEY, 'true')
    }
    window.addEventListener('onzoe:tour-event', handleTourEvent)
    return () => window.removeEventListener('onzoe:tour-event', handleTourEvent)
  }, [])

  useEffect(() => {
    const isDashboard = location.pathname === '/dashboard'
    const launcher = document.getElementById('onzoe-tour-launcher')
    if (!isDashboard) {
      if (launcher) launcher.style.display = 'none'
      return
    }
    const initialize = async () => {
      if (!window.OnzoeWidget) return
      initialized.current = true
      await window.OnzoeWidget.init({
        widgetId: 'onzoe-dashboard-product-tour',
        tourOnly: true,
        tours: [dashboardTour()],
        tourNarrationEndpoint: '/api/tours/dashboard/narration',
        tourEventEndpoint: '/api/tours/dashboard/events',
        analytics: { enabled: false },
        observability: { enabled: false },
        channels: { text: false, voice: false, defaultMode: 'text' },
        brandName: 'Onzoe',
        agentName: 'Onzoe',
      })
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-onzoe-dashboard-tour="true"]')
    if (existing) {
      if (window.OnzoeWidget) void initialize()
      else existing.addEventListener('load', () => void initialize(), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = '/widget.js'
    script.async = true
    script.dataset.onzoeDashboardTour = 'true'
    script.addEventListener('load', () => void initialize(), { once: true })
    document.head.appendChild(script)
  }, [location.pathname])

  useEffect(() => {
    const start = () => window.OnzoeWidget?.startTour(TOUR_ID)
    window.addEventListener('onzoe:start-dashboard-tour', start)
    return () => window.removeEventListener('onzoe:start-dashboard-tour', start)
  }, [])

  return null
}
